import SmoothScroll from '../singleton/SmoothScroll';

const STORAGE_KEY = '8k-popup-dismissed';
/** Delay after window `load` before opening (ms) — keeps popup off the critical path. */
const OPEN_DELAY_MS = 3500;

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'textarea:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(', ');

class Popup {
	private root: HTMLElement;
	private dialog: HTMLElement;
	private backdrop: HTMLElement | null;
	private closeBtn: HTMLElement | null;
	private cta: HTMLElement | null;
	private previouslyFocused: HTMLElement | null = null;
	private isOpen = false;
	private openTimer: ReturnType<typeof setTimeout> | null = null;
	private smoothScroll: typeof SmoothScroll;

	private onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
	private onCloseClick = () => this.close();
	private onBackdropClick = () => this.close();

	constructor(el: HTMLElement) {
		this.root = el;
		this.dialog = el.querySelector('[data-popup-dialog]') as HTMLElement;
		this.backdrop = el.querySelector('[data-popup-backdrop]');
		this.closeBtn = el.querySelector('[data-popup-close]');
		this.cta = el.querySelector('[data-popup-cta]');
		this.smoothScroll = SmoothScroll;

		if (!this.dialog) return;
		if (this.isDismissed()) return;

		this.bindCloseControls();
		this.scheduleOpen();
	}

	private isDismissed(): boolean {
		try {
			return sessionStorage.getItem(STORAGE_KEY) === '1';
		} catch {
			return false;
		}
	}

	private markDismissed() {
		try {
			sessionStorage.setItem(STORAGE_KEY, '1');
		} catch {
			// sessionStorage may be unavailable; still close for this page view
		}
	}

	private bindCloseControls() {
		this.closeBtn?.addEventListener('click', this.onCloseClick);
		this.backdrop?.addEventListener('click', this.onBackdropClick);
		this.cta?.addEventListener('click', this.onCloseClick);
	}

	private scheduleOpen() {
		const start = () => {
			this.openTimer = setTimeout(() => this.open(), OPEN_DELAY_MS);
		};

		if (document.readyState === 'complete') {
			start();
		} else {
			window.addEventListener('load', start, { once: true });
		}
	}

	private getFocusable(): HTMLElement[] {
		return Array.from(
			this.dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
		).filter(
			(el) =>
				!el.hasAttribute('disabled') &&
				el.getAttribute('aria-hidden') !== 'true' &&
				el.tabIndex !== -1,
		);
	}

	private open() {
		if (this.isOpen || this.isDismissed()) return;

		this.previouslyFocused = document.activeElement as HTMLElement | null;
		this.isOpen = true;

		this.root.removeAttribute('inert');
		this.root.setAttribute('aria-hidden', 'false');

		// Next frame so CSS can transition from data-open="false" → "true"
		requestAnimationFrame(() => {
			this.root.setAttribute('data-open', 'true');
		});

		this.smoothScroll.disableScrolling();
		document.addEventListener('keydown', this.onKeyDown);

		const focusable = this.getFocusable();
		const focusTarget = this.closeBtn ?? focusable[0] ?? this.dialog;
		focusTarget.focus();
	}

	private close() {
		if (!this.isOpen) return;

		if (this.openTimer) {
			clearTimeout(this.openTimer);
			this.openTimer = null;
		}

		this.isOpen = false;
		this.markDismissed();

		this.root.setAttribute('data-open', 'false');
		this.root.setAttribute('aria-hidden', 'true');
		this.root.setAttribute('inert', '');

		document.removeEventListener('keydown', this.onKeyDown);
		this.smoothScroll.enableScrolling();

		this.previouslyFocused?.focus?.();
		this.previouslyFocused = null;
	}

	private handleKeyDown(e: KeyboardEvent) {
		if (!this.isOpen) return;

		if (e.key === 'Escape') {
			e.preventDefault();
			this.close();
			return;
		}

		if (e.key !== 'Tab') return;

		const focusable = this.getFocusable();
		if (!focusable.length) {
			e.preventDefault();
			this.dialog.focus();
			return;
		}

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement as HTMLElement | null;

		if (e.shiftKey) {
			if (active === first || !this.dialog.contains(active)) {
				e.preventDefault();
				last.focus();
			}
		} else if (active === last) {
			e.preventDefault();
			first.focus();
		}
	}
}

export default Popup;
